import { call, put, fork, takeEvery, select, race, take } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { normalize } from 'normalizr';
import { push } from 'react-router-redux';
import { show, hide } from 'redux-modal';
import moment from 'moment';
import { startSubmit, stopSubmit } from 'redux-form';

import * as schemas from 'store/common/schemas';
import * as actionTypes from './action-types';
import * as actions from './actions';
import * as entityActions from 'store/entities/actions';
import * as authActions from 'store/features/auth/actions';
import { linkedInLogin } from 'store/features/auth/saga';
import { selectCompanyByPrettyId } from 'store/features/companies/selectors';
import { selectUserAdvisories, selectAdvisory } from './selectors';
import callApi from 'store/api/saga';
import * as api from 'api/config/users';
import {
  selectCurrentUser,
  selectCurrentUserId,
  selectCurrentUserEthereumAddress,
} from 'store/features/auth/selectors';
import routeGenerators from 'ui/common/routes/generators';
import { MODAL_NAME as SET_ETHEREUM_ADDRESS_MODAL_NAME } from 'ui/users/set-eth-address/index';
import { SET_ETHEREUM_ADDRESS } from 'store/features/users/action-types';
import {
  connectionActions as connectionActionsMessages,
  softConnection as softConnectionMessages,
} from 'ui/users/advisories/messages';
import { confirmSaga as confirm } from 'store/features/common/confirmation-dialog/saga';
import { addMessage } from 'store/features/common/flash/actions';
import { messageTypes } from 'store/features/common/flash/builder';
import { ADVISOR_CONNECTION_STATUSES } from 'store/features/company-advisor-connections/helpers';

window.moment = moment;
const MODAL_HIDE = '@redux-modal/HIDE';

const MAX_POLL_TIME_IN_SECONDS =
  Number(process.env.REACT_APP_MAX_CONNECTIONS_POLL_TIME_IN_SECONDS) || 60;
const POLL_INTERVAL_IN_SECONDS =
  Number(process.env.REACT_APP_CONNECTIONS_POLL_INTERVAL_IN_SECONDS) || 10;

function* pollAdvisoryStatus({
  prettyId,
  advisoryId,
  timeoutStartsAt,
  startStatuses = [],
  stopStatuses = [],
}) {
  if (!prettyId || !advisoryId || !timeoutStartsAt) return false;

  let advisory = yield select(selectAdvisory, { advisoryId });
  if (!advisory || startStatuses.indexOf(advisory.status) === -1) return false;

  const stopAt = moment(timeoutStartsAt).add(MAX_POLL_TIME_IN_SECONDS, 'seconds');
  yield put(actions.pollCurrentUserAdvisory.start({ prettyId, advisoryId }));
  while (moment() < stopAt) {
    yield call(fetchAdvisory, { payload: { prettyId, advisoryId } });
    advisory = yield select(selectAdvisory, { advisoryId });

    if (stopStatuses.indexOf(advisory.status) >= 0) {
      yield put(actions.pollCurrentUserAdvisory.stop({ prettyId, advisoryId }));
      return true;
    }

    yield delay(POLL_INTERVAL_IN_SECONDS * 1000);
  }

  yield put(actions.pollCurrentUserAdvisory.stop({ prettyId, advisoryId }));
  return false;
}

function* checkCompanySideTransaction({ advisory }) {
  if (advisory.id && advisory.company && advisory.company.prettyId) {
    if (advisory.status === ADVISOR_CONNECTION_STATUSES.pendingTransactionSigningByCompany) {
      try {
        yield call(
          callApi,
          api.acceptAdvisory({
            companyPrettyId: advisory.company.prettyId,
            advisoryId: advisory.id,
          })
        );
      } catch (error) {
        console.error(error);
      }
    }

    yield fork(pollAdvisoryStatus, {
      advisoryId: advisory.id,
      prettyId: advisory.company.prettyId,
      timeoutStartsAt: advisory.acceptedTimestamp,
      startStatuses: [ADVISOR_CONNECTION_STATUSES.pendingTransactionSigningByCompany],
      stopStatuses: [ADVISOR_CONNECTION_STATUSES.signedByBoth],
    });
  }
}

function* fetchUserAdvisories({ payload }) {
  const { userId } = payload;
  const currentUserId = yield select(selectCurrentUserId);
  yield put(actions.fetchUserAdvisories.start({ userId }));

  try {
    let response = yield call(
      callApi,
      currentUserId === userId
        ? api.fetchCurrentUserAdvisories()
        : api.fetchUserAdvisories({ userId })
    );
    response = response.map(item => {
      item.user = { id: userId };
      return item;
    });

    const schema = [schemas.companyAdvisorConnection];
    const { entities, result } = normalize(response, schema);
    yield put(entityActions.mergeEntities(entities));

    yield put(actions.fetchUserAdvisories.success({ userId, advisories: result }));

    if (userId === currentUserId) {
      const advisories = yield select(selectUserAdvisories, { userId });
      for (let advisoryIndex = 0; advisoryIndex < advisories.length; advisoryIndex++) {
        const advisory = advisories[advisoryIndex];
        yield fork(checkCompanySideTransaction, { advisory });
      }
    }
  } catch (error) {
    yield put(actions.fetchUserAdvisories.failure({ userId, error: error.message }));
  }
}

function* watchFetchUserAdvisories() {
  yield takeEvery(actionTypes.FETCH_ADVISORIES_BY_USER_ID.REQUEST, fetchUserAdvisories);
}

function* fetchAdvisory({ payload }) {
  const { prettyId, advisoryId } = payload;
  yield put(actions.fetchAdvisory.start({ prettyId, advisoryId }));

  try {
    const response = yield call(callApi, api.fetchAdvisory({ prettyId, advisoryId }));
    response.company = { prettyId };

    const schema = schemas.companyAdvisorConnection;
    const { entities } = normalize(response, schema);
    yield put(entityActions.mergeEntities(entities));

    yield put(actions.fetchAdvisory.success({ prettyId, advisoryId }));
  } catch (error) {
    yield put(actions.fetchAdvisory.failure({ prettyId, advisoryId, error: error.message }));
  }
}

function* watchFetchAdvisory() {
  yield takeEvery(actionTypes.FETCH_ADVISORY.REQUEST, fetchAdvisory);
}

function* checkAcceptAdvisoryStatus({ payload }) {
  const { prettyId, advisoryId } = payload;
  const currentUserId = yield select(selectCurrentUserId);
  const currentUserEthereumAddress = yield select(selectCurrentUserEthereumAddress);

  yield put(actions.checkAcceptAdvisoryStatus.start({ prettyId, advisoryId }));
  try {
    if (currentUserId && !currentUserEthereumAddress) {
      yield put(show(SET_ETHEREUM_ADDRESS_MODAL_NAME));
      const { ethAddressSet } = yield race({
        ethAddressSet: take(
          action =>
            action &&
            action.type === SET_ETHEREUM_ADDRESS.SUCCESS &&
            action.payload &&
            action.payload.userId === currentUserId
        ),
        ethAddressModalClosed: take(
          action =>
            action &&
            action.type === MODAL_HIDE &&
            action.payload &&
            action.payload.modal === SET_ETHEREUM_ADDRESS_MODAL_NAME
        ),
      });
      // Safety net
      yield put(hide(SET_ETHEREUM_ADDRESS_MODAL_NAME));
      if (!ethAddressSet) {
        yield put(
          actions.checkAcceptAdvisoryStatus.failure({
            error: 'Eth address not set',
            prettyId,
            advisoryId,
          })
        );
        return;
      }
    }

    const response = yield call(
      callApi,
      api.acceptAdvisory({ companyPrettyId: prettyId, advisoryId })
    );

    const responseStatus = response && response.statusCode;
    const responseMessage = response && response.message;

    yield put(authActions.setHasConnectionsEntity(responseStatus && responseStatus > 1));

    yield put(actions.checkAcceptAdvisoryStatus.success({ prettyId, advisoryId }));

    if (responseStatus === 2) throw new Error(responseMessage);
    if (responseStatus === 5) {
      yield call(checkAcceptAdvisoryStatus, { payload: { advisoryId } });
      yield put(addMessage({ kind: messageTypes.success, content: responseMessage }));
    } else yield put(push(routeGenerators.companies.acceptAsAdvisor({ prettyId, advisoryId })));
  } catch (error) {
    yield put(addMessage({ kind: messageTypes.danger, content: error.message }));
    yield put(
      actions.checkAcceptAdvisoryStatus.failure({ error: error.message, prettyId, advisoryId })
    );
  }
}

function* watchCheckAcceptAdvisoryStatus() {
  yield takeEvery(actionTypes.CHECK_ACCEPT_ADVISORY_STATUS.REQUEST, checkAcceptAdvisoryStatus);
}

function* watchShowAcceptAdvisoryInstructions() {
  yield takeEvery(actionTypes.SHOW_ACCEPT_ADVISORY_INSTRUCTIONS, checkAcceptAdvisoryStatus);
}

function* completeAcceptAdvisory({ prettyId, advisoryId, txHash }) {
  try {
    yield call(fetchAdvisory, { payload: { prettyId, advisoryId } });
    const advisory = yield select(selectAdvisory, { advisoryId });

    yield call(pollAdvisoryStatus, {
      prettyId,
      advisoryId,
      timeoutStartsAt: advisory.acceptedTimestamp || advisory.creationTimestamp,
      startStatuses: [
        ADVISOR_CONNECTION_STATUSES.pendingTransactionSigningByBoth,
        ADVISOR_CONNECTION_STATUSES.pendingTransactionSigningByCompany,
        ADVISOR_CONNECTION_STATUSES.pendingTransactionSigningByUser,
      ],
      stopStatuses: [ADVISOR_CONNECTION_STATUSES.signedByBoth],
    });

    yield call(callApi, api.acceptAdvisory({ companyPrettyId: prettyId, advisoryId, txHash }));
  } catch (error) {
    yield put(actions.acceptAdvisory.failure({ error: error.message, advisoryId, txHash }));
    yield put(
      addMessage({
        kind: messageTypes.danger,
        content: error.message,
      })
    );
  }
}

function* acceptAdvisory({ payload, meta = {} }) {
  let { advisoryId, txHash } = payload;
  let advisory = yield select(selectAdvisory, { advisoryId });
  const { form } = meta;
  const { company: { prettyId } } = advisory;

  yield put(actions.acceptAdvisory.start({ advisoryId, txHash }));
  if (form) yield put(startSubmit(form));

  try {
    yield call(callApi, api.acceptAdvisory({ companyPrettyId: prettyId, advisoryId, txHash }));

    yield call(fetchAdvisory, { payload: { prettyId, advisoryId } });
    advisory = yield select(selectAdvisory, { advisoryId });

    yield put(actions.acceptAdvisory.success({ advisoryId, txHash }));
    if (form) yield put(stopSubmit(form));
  } catch (error) {
    yield put(actions.acceptAdvisory.failure({ error: error.message, advisoryId, txHash }));
    if (form) yield put(stopSubmit(form, error.formErrors));
  } finally {
    const currentUser = yield select(selectCurrentUser);
    const profileUrl = routeGenerators.users.bestProfile({ user: currentUser });
    yield put(push(profileUrl));

    yield call(completeAcceptAdvisory, { prettyId, advisoryId, txHash });
  }
}

function* watchAcceptAdvisory() {
  yield takeEvery(actionTypes.ACCEPT_ADVISORY.REQUEST, acceptAdvisory);
}

//#region "Reject"
function* rejectAdvisory({ payload }) {
  const { advisoryId } = payload;
  const advisory = yield select(selectAdvisory, { advisoryId });
  const { company } = advisory;

  const confirmationMessage = {
    ...connectionActionsMessages.confirmReject,
    values: { companyName: company.companyName },
  };
  const confirmed = yield call(confirm, { message: confirmationMessage });
  if (!confirmed) {
    return;
  }

  yield put(actions.rejectAdvisory.start({ advisoryId }));

  try {
    yield call(callApi, api.rejectAdvisory({ companyPrettyId: company.prettyId, advisoryId }));
    // Ideally api should return modified advisory. But for now, we would mark rejected as true here
    yield put(actions.rejectAdvisory.success({ advisoryId }));
    yield put(
      addMessage({
        kind: messageTypes.success,
        content: {
          ...connectionActionsMessages.rejected,
          values: { companyName: company.companyName },
        },
      })
    );
  } catch (error) {
    yield put(actions.rejectAdvisory.failure({ error: error.message, advisory }));
  }
}

function* watchRejectAdvisory() {
  yield takeEvery(actionTypes.REJECT_ADVISORY.REQUEST, rejectAdvisory);
}

//#endregion

//#region Revoke
function* showRevokeAdvisoryInstructions({ payload }) {
  const { advisoryId } = payload;
  const advisory = yield select(selectAdvisory, { advisoryId });
  const { company: { prettyId } } = advisory;

  const confirmationMessage = {
    ...connectionActionsMessages.confirmRevoke,
    values: { companyName: prettyId },
  };
  const confirmed = yield call(confirm, { message: confirmationMessage });
  if (!confirmed) {
    return;
  }

  // check the status
  if (
    advisory.status === ADVISOR_CONNECTION_STATUSES.signedByBoth ||
    advisory.status === ADVISOR_CONNECTION_STATUSES.pendingTransactionSigningByCompany
  ) {
    yield put(
      push(routeGenerators.companies.revokeAsAdvisor({ prettyId, advisoryId: advisory.id }))
    );
  } else {
    // TODO: show msg
    return;
  }
}

function* watchShowRevokeAdvisoryInstructions() {
  yield takeEvery(actionTypes.SHOW_REVOKE_ADVISORY_INSTRUCTIONS, showRevokeAdvisoryInstructions);
}

function* revokeAdvisory({ payload, meta = {} }) {
  let { advisoryId, txHash } = payload;
  const advisory = yield select(selectAdvisory, { advisoryId });
  const { form } = meta;
  const { company: { prettyId } } = advisory;
  const currentUser = yield select(selectCurrentUser);

  yield put(actions.revokeAdvisory.start({ advisoryId, txHash }));

  if (form) yield put(startSubmit(form));
  try {
    const profileUrl = routeGenerators.users.bestProfile({ user: currentUser });
    yield put(push(profileUrl));

    const revoked = yield call(pollAdvisoryStatus, {
      prettyId,
      advisoryId,
      timeoutStartsAt: moment(),
      startStatuses: [ADVISOR_CONNECTION_STATUSES.signedByBoth],
      stopStatuses: [ADVISOR_CONNECTION_STATUSES.pendingTransactionSigningByUser],
    });

    if (revoked) {
      yield call(
        callApi,
        api.revokeAdvisory({ companyPrettyId: prettyId, advisoryId: advisoryId, txHash: txHash })
      );

      const schema = schemas.companyAdvisorConnection;
      yield put(entityActions.removeEntities({ [schema.key]: [advisoryId] }));
      yield put(actions.revokeAdvisory.success({ advisoryId, txHash }));
      if (form) yield put(stopSubmit(form));

      yield put(
        addMessage({
          kind: messageTypes.success,
          content: {
            ...connectionActionsMessages.revoked,
            values: { companyName: prettyId },
          },
        })
      );
    } else {
      if (form) yield put(stopSubmit(form));
      // TODO: Decide to error message or not
      yield put(
        actions.revokeAdvisory.failure({ error: 'Transaction is not mined', advisoryId, txHash })
      );
    }
  } catch (error) {
    yield put(actions.revokeAdvisory.failure({ error: error.message, advisoryId, txHash }));
  }
}

function* watchRevokeAdvisory() {
  yield takeEvery(actionTypes.REVOKE_ADVISORY.REQUEST, revokeAdvisory);
}
//#endregion

//#region "softReject"
function* softRejectAdvisory({ payload }) {
  const { advisoryId, companyPrettyId } = payload;
  const company = yield select(selectCompanyByPrettyId, { prettyId: companyPrettyId });

  const confirmationMessage = {
    ...softConnectionMessages.confirmReject,
    values: { companyName: company.companyName },
  };
  const confirmed = yield call(confirm, { message: confirmationMessage });
  if (!confirmed) {
    return;
  }

  yield put(actions.softRejectAdvisory.start({ advisoryId }));

  try {
    const response = yield call(
      callApi,
      api.softRejectAdvisory({ companyPrettyId: company.prettyId, advisoryId })
    );

    response.id = advisoryId;
    response.company = { prettyId: company.prettyId };
    const schema = schemas.companyAdvisorConnection;
    const { entities } = normalize(response, schema);
    yield put(entityActions.mergeEntities(entities));

    const softConnection = response && response.softConnection;
    yield put(actions.softRejectAdvisory.success({ advisoryId, softConnection }));
  } catch (error) {
    yield put(addMessage({ kind: messageTypes.danger, content: error.message }));
    yield put(actions.softRejectAdvisory.failure({ advisoryId, error: error.message }));
  }
}

function* watchSoftRejectAdvisory() {
  yield takeEvery(actionTypes.SOFT_REJECT_ADVISORY.REQUEST, softRejectAdvisory);
}

//#endregion

//#region "softAccept"
function* softAcceptAdvisory({ payload }) {
  const { advisoryId, companyPrettyId } = payload;
  const company = yield select(selectCompanyByPrettyId, { prettyId: companyPrettyId });

  yield put(actions.softAcceptAdvisory.start({ advisoryId }));

  try {
    const response = yield call(
      callApi,
      api.softAcceptAdvisory({ companyPrettyId: company.prettyId, advisoryId })
    );

    response.id = advisoryId;
    response.company = { prettyId: company.prettyId };
    const schema = schemas.companyAdvisorConnection;
    const { entities } = normalize(response, schema);
    yield put(entityActions.mergeEntities(entities));

    const softConnection = response && response.softConnection;
    yield put(actions.softAcceptAdvisory.success({ advisoryId, softConnection }));
  } catch (error) {
    yield put(addMessage({ kind: messageTypes.danger, content: error.message }));
    yield put(actions.softAcceptAdvisory.failure({ advisoryId, error: error.message }));
  }
}

function* watchSoftAcceptAdvisory() {
  yield takeEvery(actionTypes.SOFT_ACCEPT_ADVISORY.REQUEST, softAcceptAdvisory);
}

function* softValidateAdvisoryUsingLinkedin({ payload }) {
  const { advisoryId, companyPrettyId, linkedIn } = payload;

  yield put(
    actions.softValidateAdvisoryUsingLinkedin.start({ advisoryId, companyPrettyId, linkedIn })
  );

  try {
    // get the token from linkedin
    const authData = yield call(linkedInLogin);

    // pass the token to our endpoint which takes care of validating and creating/linking user a/c
    const response = yield call(
      callApi,
      api.softValidateAdvisoryUsingLinkedin({
        companyPrettyId: companyPrettyId,
        advisoryId,
        linkedIn: authData,
      })
    );

    const token = response ? response.token : null;
    response.softConnection = {
      statusValidatedByUserAt: new Date().valueOf(),
      token,
    };

    response.id = advisoryId;
    response.company = { prettyId: companyPrettyId };
    const schema = schemas.companyAdvisorConnection;
    const { entities } = normalize(response, schema);
    yield put(entityActions.mergeEntities(entities));

    yield put(actions.softValidateAdvisoryUsingLinkedin.success({ advisoryId }));
  } catch (error) {
    const { message } = error;
    const msg = error.message || (message && message.linkedInAuthError);
    if (msg) {
      yield put(addMessage({ kind: messageTypes.danger, content: error.message }));
    }
    yield put(
      actions.softValidateAdvisoryUsingLinkedin.failure({ advisoryId, error: error.message })
    );
  }
}

function* watchSoftValidateAdvisoryUsingLinkedin() {
  yield takeEvery(
    actionTypes.SOFT_VALIDATE_ADVISORY_USING_LINKEDIN.REQUEST,
    softValidateAdvisoryUsingLinkedin
  );
}

//#endregion

export default function* advisories() {
  yield fork(watchFetchUserAdvisories);
  yield fork(watchCheckAcceptAdvisoryStatus);
  yield fork(watchShowAcceptAdvisoryInstructions);
  yield fork(watchAcceptAdvisory);
  yield fork(watchRejectAdvisory);
  yield fork(watchRevokeAdvisory);
  yield fork(watchShowRevokeAdvisoryInstructions);
  yield fork(watchFetchAdvisory);
  yield fork(watchSoftAcceptAdvisory);
  yield fork(watchSoftRejectAdvisory);
  yield fork(watchSoftValidateAdvisoryUsingLinkedin);
}



// WEBPACK FOOTER //
// ./src/store/features/users/advisories/saga.js